import { US_STATE_CODE_ALTERNATION } from "../constants/usStates";

export type PatientCareRelationship = "me" | "parent" | "spouse" | "other";

export interface PatientCareJourney {
  id: string;
  relationship: PatientCareRelationship;
  locationLabel?: string | null;
  locationZip?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export const PATIENT_CARE_RELATIONSHIP_LABELS: Record<
  PatientCareRelationship,
  string
> = {
  me: "My care",
  parent: "Parent's care",
  spouse: "Spouse's care",
  other: "Family member's care",
};

const ZIP_PATTERN = /\b(\d{5})(?:-\d{4})?\b/g;

/**
 * Extract a US ZIP from free text.
 *
 * Mirrors backend GeocodingUtils / location_hint_utils priorities:
 * 1) ZIP after a USPS state abbreviation
 * 2) ZIP at end of string
 * 3) Other mid-string ZIPs, never a leading house number before a street token
 *    (e.g. "11247 Creekside Trail…" must not become ZIP 11247 → NY)
 */
export function extractZipFromLocation(location?: string | null): string | null {
  if (!location?.trim()) {
    return null;
  }
  const text = location.trim();
  const upper = text.toUpperCase();

  // Prefer ZIP immediately after a state code ("MN 55311", ", MN 55311-1234").
  const afterState = upper.match(
    new RegExp(
      `\\b(?:${US_STATE_CODE_ALTERNATION})\\s+(\\d{5})(?:-\\d{4})?\\b`
    )
  );
  if (afterState?.[1]) {
    return afterState[1];
  }

  const atEnd = text.match(/\b(\d{5})(?:-\d{4})?\s*$/);
  if (atEnd?.[1]) {
    return atEnd[1];
  }

  for (const match of text.matchAll(ZIP_PATTERN)) {
    const start = match.index ?? 0;
    const before = text.slice(0, start).trim();
    const after = text.slice(start + match[0].length).trimStart();
    // Leading house number: nothing before, street/name token after.
    if (!before && after && /^[A-Za-z"'`]/.test(after)) {
      continue;
    }
    return match[1] ?? null;
  }
  return null;
}

export function formatPatientJourneyResumeLabel(
  journey: Pick<PatientCareJourney, "relationship" | "locationLabel">
): string {
  const relationshipLabel =
    PATIENT_CARE_RELATIONSHIP_LABELS[journey.relationship] ?? "My care";
  if (journey.locationLabel?.trim()) {
    return `${relationshipLabel} · ${journey.locationLabel.trim()}`;
  }
  return relationshipLabel;
}

export function locationKeyFromSearchCriteria(criteria: {
  location?: string;
  location_query?: string;
}): string {
  const location =
    criteria.location?.trim() || criteria.location_query?.trim() || "";
  return locationKeyFromLabel(location);
}

/** Canonical location key — mirrors backend `normalize_location_key`. */
export function locationKeyFromLabel(
  locationLabel?: string | null,
  locationZip?: string | null
): string {
  if (locationZip?.trim()) {
    return `zip:${locationZip.trim()}`;
  }
  const zip = extractZipFromLocation(locationLabel);
  if (zip) {
    return `zip:${zip}`;
  }
  if (locationLabel?.trim()) {
    return `loc:${locationLabel.trim().toLowerCase()}`;
  }
  return "unknown";
}

export function locationKeyFromJourney(
  journey: Pick<PatientCareJourney, "locationLabel" | "locationZip">
): string {
  return locationKeyFromLabel(journey.locationLabel, journey.locationZip);
}

export function locationKeyFromProviderAddress(provider: {
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
}): { locationKey: string; locationLabel?: string } {
  const zip = provider.zip_code?.trim() || null;
  const city = provider.city?.trim();
  const state = provider.state?.trim();
  const locationLabel =
    city && state
      ? zip
        ? `${city}, ${state} ${zip}`
        : `${city}, ${state}`
      : zip
        ? zip
        : undefined;
  return {
    locationKey: locationKeyFromLabel(locationLabel, zip),
    locationLabel,
  };
}

export function resolvePatientResumeBannerContext(options: {
  resumeSearchCriteria?: { location?: string; location_query?: string };
  journey?: PatientCareJourney | null;
  pendingLocationLabel?: string | null;
  hasPendingRelationship?: boolean;
}): {
  subjectLabel: string;
  contextId?: string;
  contextType: "journey";
} | null {
  const {
    resumeSearchCriteria,
    journey,
    pendingLocationLabel,
    hasPendingRelationship = false,
  } = options;
  if (!resumeSearchCriteria) {
    return null;
  }

  if (journey) {
    return {
      subjectLabel: formatPatientJourneyResumeLabel(journey),
      contextId: journey.id,
      contextType: "journey",
    };
  }

  const searchLocation =
    resumeSearchCriteria.location?.trim() ||
    resumeSearchCriteria.location_query?.trim() ||
    pendingLocationLabel?.trim();
  if (!searchLocation) {
    return null;
  }

  if (hasPendingRelationship) {
    return {
      subjectLabel: searchLocation,
      contextType: "journey",
    };
  }

  return {
    subjectLabel: formatPatientJourneyResumeLabel({
      relationship: "me",
      locationLabel: searchLocation,
    }),
    contextType: "journey",
  };
}

export function shouldStartNewJourneyForLocation(
  activeJourney: Pick<PatientCareJourney, "locationLabel" | "locationZip"> | null,
  nextLocationKey: string
): boolean {
  if (nextLocationKey === "unknown") {
    return false;
  }
  if (!activeJourney) {
    return false;
  }
  const currentKey = locationKeyFromJourney(activeJourney);
  return currentKey !== "unknown" && currentKey !== nextLocationKey;
}

/** Whether the patient must confirm who the search is for before journey sync. */
export function formatPatientJourneyContinuePrompt(
  relationship: PatientCareRelationship,
  locationLabel?: string | null
): string {
  const relationshipLabel =
    PATIENT_CARE_RELATIONSHIP_LABELS[relationship] ?? "My care";
  if (locationLabel?.trim()) {
    return `Still searching for ${relationshipLabel.toLowerCase()} near ${locationLabel.trim()}?`;
  }
  return `Still searching for ${relationshipLabel.toLowerCase()}?`;
}

export function shouldQueueCareJourneyRelationshipConfirmation(
  activeJourney: Pick<PatientCareJourney, "locationLabel" | "locationZip"> | null,
  nextLocationKey: string
): boolean {
  if (nextLocationKey === "unknown") {
    return false;
  }
  if (!activeJourney) {
    return true;
  }
  return shouldStartNewJourneyForLocation(activeJourney, nextLocationKey);
}
